import { useState } from "react";
import { MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { CostConfirm } from "./CostConfirm";

const REDO = {
    visuals: { label: "Redo visuals", paths: ["regenerate-visual"], action: "redo_visuals" },
    voice: { label: "Redo voice", paths: ["regenerate-voice"], action: "redo_voice" },
    both: { label: "Redo both (visuals and voice)", paths: ["regenerate-visual", "regenerate-voice"], action: "redo_both" },
};

// Per-scene advanced menu. Any edit returns the scene to "Needs review".
export function SceneMenu({ scene, moduleId, onChanged, onPreview, onFixWithAI }) {
    const [confirm, setConfirm] = useState(null);
    const [promptOpen, setPromptOpen] = useState(false);
    const [text, setText] = useState(scene.custom_prompt || "");
    const n = scene.scene_number;

    const backToReview = () => api.post(`/scenes/${scene.id}/unapprove`).catch(() => null);
    const redo = (kind) => setConfirm({
        title: `${REDO[kind].label} for scene ${n}?`,
        description: `The scene goes back to “Needs review” afterwards.${scene.custom_prompt ? " Your custom prompt is used." : ""}`,
        estimate: { moduleId: moduleId || scene.module_id, action: REDO[kind].action, sceneId: scene.id },
        okLabel: REDO[kind].label,
        onConfirm: async () => {
            try {
                await backToReview();
                for (const path of REDO[kind].paths) await api.post(`/scenes/${scene.id}/${path}`);
                toast.success(`Scene ${n}: ${REDO[kind].label.toLowerCase()} started`, { duration: 8000 });
                onChanged && onChanged();
            } catch (e) { toast.error(formatApiError(e)); }
        },
    });
    const savePrompt = async () => {
        try {
            await api.post(`/scenes/${scene.id}/custom-prompt`, { prompt: text });
            await backToReview();
            setPromptOpen(false);
            toast.success(`Scene ${n}: custom prompt saved. Use a “Redo” option to apply it.`, { duration: 8000 });
            onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const undoPrompt = async () => {
        try {
            const { data } = await api.post(`/scenes/${scene.id}/custom-prompt/undo`);
            setText(data.custom_prompt || "");
            await backToReview();
            toast.success(`Scene ${n}: previous prompt restored`, { duration: 8000 });
            onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <button type="button" data-testid={`scene-menu-${n}`} className="rounded p-0.5 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-900" aria-label={`Scene ${n} options`}>
                        <MoreHorizontal size={14} />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    {onPreview && <DropdownMenuItem data-testid={`scene-menu-preview-${n}`} onSelect={() => onPreview(scene)}>Preview scene</DropdownMenuItem>}
                    {onFixWithAI && <DropdownMenuItem data-testid={`scene-menu-ai-fix-${n}`} onSelect={() => onFixWithAI(scene)}>Fix with AI</DropdownMenuItem>}
                    {(onPreview || onFixWithAI) && <DropdownMenuSeparator />}
                    <DropdownMenuLabel>Redo this scene</DropdownMenuLabel>
                    {Object.keys(REDO).map((k) => (
                        <DropdownMenuItem key={k} data-testid={`scene-menu-redo-${k}-${n}`} onSelect={() => redo(k)}>{REDO[k].label}</DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid={`scene-menu-custom-prompt-${n}`} onSelect={() => setPromptOpen(true)}>Custom prompt for this scene…</DropdownMenuItem>
                    <DropdownMenuItem data-testid={`scene-menu-undo-prompt-${n}`} disabled={!(scene.custom_prompt_history || []).length} onSelect={undoPrompt}>Undo prompt change</DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <Dialog open={promptOpen} onOpenChange={setPromptOpen}>
                <DialogContent data-testid={`scene-custom-prompt-dialog-${n}`} onClick={(e) => e.stopPropagation()}>
                    <DialogHeader>
                        <DialogTitle>Custom prompt · scene {n}</DialogTitle>
                        <DialogDescription>A short direction that steers this scene only. Leave empty to follow the video settings.</DialogDescription>
                    </DialogHeader>
                    <Textarea data-testid={`scene-custom-prompt-input-${n}`} rows={4} maxLength={600} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Focus on the gauge close-up; calmer tone" />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPromptOpen(false)} data-testid={`scene-custom-prompt-cancel-${n}`}>Cancel</Button>
                        <Button onClick={savePrompt} data-testid={`scene-custom-prompt-save-${n}`} className="bg-neutral-900 text-white hover:bg-neutral-800">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <CostConfirm confirm={confirm} onClose={() => setConfirm(null)} />
        </>
    );
}
